"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { TopOperationalBar } from "./top-operational-bar";
import { OperationalTruthRail } from "./operational-truth-rail";
import { ContextualActionBar } from "./contextual-action-bar";
import { MainOperationalConsole } from "./screens/main-operational-console";
import { CommandAura } from "./screens/command-aura";
import { DecisionRoom } from "./screens/decision-room";
import { AgentStudio } from "./screens/agent-studio";
import { ToolsStudio } from "./screens/tools-studio";
import { FutureOperationalSurface } from "./screens/future-operational-surface";
import type {
  AgentKind,
  AgentProfile,
  AgentRegistrationStatus,
  AgentType,
  ConversationMessage,
} from "@/lib/aura/types";
import { operationalState } from "@/src/data/operational-state";
import { adaptLiveOperationalState } from "@/src/data/live-operational-state";
import {
  askAuraConversation,
  appendAdminToolLearningEvidence,
  createAdminAgent,
  createAdminTool,
  createAdminToolAssignment,
  createAdminToolConsolidationRun,
  createAdminToolLearningSession,
  createCommandSessionId,
  dispatchNextOperatorCommand,
  loadAdminAgentRegistry,
  loadAdminTools,
  loadAuraConversationHistory,
  queueOperatorCommand,
  recordDecisionContribution,
  registerAdminAgent,
  selectDecisionRoomAgentCandidates,
  refreshAdminConsoleSnapshot,
  promoteAdminToolLearningMemory,
  uploadAdminToolLearningArtifact,
  type AdminAgentCreateInput,
  type AdminToolCreateInput,
  type AdminToolLearningArtifactStoreInput,
  type AdminToolLearningEvidenceAppendInput,
  type AdminToolLearningMemoryPromotionInput,
  type AdminToolLearningSessionCreateInput,
  type AdminToolsState,
} from "@/src/data/admin-console-api";
import type { PrimaryView } from "./contextual-action-bar";

type FutureView = (typeof operationalState.futureSurfaces)[number]["id"];
type View = PrimaryView | FutureView;

export function AuraAppShell() {
  const [state, setState] = useState(operationalState);
  const [activeView, setActiveView] = useState<View>("console");
  const [selectedAgentId, setSelectedAgentId] = useState<AgentType | "all">(
    state.commandThreads.selectedAgentId
  );
  const [commandAgentContext, setCommandAgentContext] = useState<AgentProfile | null>(
    null
  );
  const [selectedTopicId, setSelectedTopicId] = useState<string | null>(
    state.decisionRoom.selectedTopicId
  );
  const [selectedAgentProfileId, setSelectedAgentProfileId] = useState<string | null>(
    state.agentRegistry.selectedAgentId
  );
  const [agentFilterStatus, setAgentFilterStatus] = useState<
    AgentRegistrationStatus | "all"
  >(state.agentRegistry.filterStatus);
  const [agentFilterKind, setAgentFilterKind] = useState<AgentKind | "all">(
    state.agentRegistry.filterKind
  );
  const [toolsState, setToolsState] = useState<AdminToolsState | null>(null);
  const [selectedToolId, setSelectedToolId] = useState<string | null>(null);
  const [toolsLoading, setToolsLoading] = useState(false);
  const [localCommandMessages, setLocalCommandMessages] = useState<
    ConversationMessage[]
  >([]);
  const [localDecisionMessages, setLocalDecisionMessages] = useState<
    ConversationMessage[]
  >([]);
  const [actionStatus, setActionStatus] = useState<string | null>(null);
  const [commandPending, setCommandPending] = useState(false);
  const [liveStateReady, setLiveStateReady] = useState(false);
  const [liveStateError, setLiveStateError] = useState<string | null>(null);
  const futureSurface = state.futureSurfaces.find(
    (surface) => surface.id === activeView
  );

  const mergeLocalMessages = useCallback(
    (
      nextState: typeof operationalState,
      commandMessages = localCommandMessages,
      decisionMessages = localDecisionMessages
    ): typeof operationalState => ({
      ...nextState,
      commandThreads: {
        ...nextState.commandThreads,
        thread: nextState.commandThreads.thread,
      },
      decisionRoom: {
        ...nextState.decisionRoom,
        thread: dedupeMessages([
          ...nextState.decisionRoom.thread,
          ...decisionMessages,
        ]),
      },
    }),
    [localDecisionMessages]
  );

  const refreshOperationalState = useCallback(async () => {
    const response = await fetch("/operational-state.json", {
      cache: "no-store",
    });

    if (!response.ok) {
      throw new Error(`operational_state_unavailable_${response.status}`);
    }

    const liveState = await response.json();
    const adapted = adaptLiveOperationalState(liveState, operationalState);
    const merged = mergeLocalMessages(adapted);

    setState((current) => ({
      ...merged,
      agentRegistry: current.agentRegistry,
    }));
    setLiveStateReady(true);
    setLiveStateError(null);
    setSelectedTopicId((current) => current ?? merged.decisionRoom.selectedTopicId);
  }, [mergeLocalMessages]);

  useEffect(() => {
    let cancelled = false;

    if (commandPending) {
      return () => {
        cancelled = true;
      };
    }

    refreshOperationalState().catch(() => {
      if (!cancelled) {
        const degradedState = createCoreUnavailableState(
          "Core truth is temporarily unavailable."
        );
        setState(degradedState);
        setLiveStateReady(true);
        setLiveStateError("Core truth is temporarily unavailable.");
        setActionStatus(
          "Core truth is temporarily unavailable; the console is open in degraded mode."
        );
      }
    });

    const interval = window.setInterval(() => {
      refreshOperationalState().catch(() => {
        if (!cancelled) {
          setLiveStateError("Live refresh could not reach Core truth.");
          setActionStatus("Live refresh could not reach Core truth.");
        }
      });
    }, 10_000);
    const eventSource =
      typeof window.EventSource === "function"
        ? new window.EventSource("/admin-console/events")
        : null;

    eventSource?.addEventListener("snapshot", () => {
      refreshOperationalState().catch(() => {
        if (!cancelled) {
          setLiveStateError("Live event received, but Core truth refresh failed.");
          setActionStatus("Live event received, but Core truth refresh failed.");
        }
      });
    });
    eventSource?.addEventListener("stream_error", () => {
      if (!cancelled) {
        setLiveStateError("Live event stream reported a snapshot refresh issue.");
      }
    });

    return () => {
      cancelled = true;
      window.clearInterval(interval);
      eventSource?.close();
    };
  }, [commandPending, refreshOperationalState]);

  useEffect(() => {
    let cancelled = false;

    loadAdminAgentRegistry()
      .then((registry) => {
        if (cancelled) return;
        setState((current) => ({
          ...current,
          agentRegistry: {
            ...current.agentRegistry,
            agents: registry.agents,
          },
        }));
      })
      .catch(() => {
        if (!cancelled) {
          setActionStatus("Agent Registry is temporarily unavailable.");
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const refreshTools = useCallback(async () => {
    setToolsLoading(true);
    try {
      const nextToolsState = await loadAdminTools();
      setToolsState(nextToolsState);
      setSelectedToolId((current) => current ?? nextToolsState.tools[0]?.id ?? null);
      return nextToolsState;
    } finally {
      setToolsLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    refreshTools()
      .catch(() => {
        if (!cancelled) {
          setActionStatus("Tools registry is temporarily unavailable.");
        }
      });

    return () => {
      cancelled = true;
    };
  }, [refreshTools]);

  useEffect(() => {
    let cancelled = false;

    loadAuraConversationHistory({
      limit: 80,
    })
      .then((history) => {
        if (cancelled) return;
        setLocalCommandMessages((current) =>
          dedupeMessages([...history.messages, ...current])
        );
      })
      .catch(() => {
        if (!cancelled) {
          setActionStatus("Command history is temporarily unavailable.");
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    loadAuraConversationHistory({
      targetAgent: selectedAgentId,
      sessionId: createCommandSessionId(selectedAgentId),
      limit: 60,
    })
      .then((history) => {
        if (cancelled) return;
        setLocalCommandMessages((current) =>
          dedupeMessages([...current, ...history.messages])
        );
      })
      .catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, [selectedAgentId]);

  const currentDecisionEvidence = useMemo(() => {
    const items = state.decisionRoom.intelligence?.evidenceItems ?? [];
    return items.slice(0, 3).map((item) => ({
      id: item.id,
      uri: `core://decision-room/evidence/${item.id}`,
      label: item.title,
    }));
  }, [state.decisionRoom.intelligence?.evidenceItems]);

  useEffect(() => {
    if (!actionStatus) return;
    const timeout = window.setTimeout(() => setActionStatus(null), 8_000);
    return () => {
      window.clearTimeout(timeout);
    };
  }, [actionStatus]);

  const routeToFutureSurface = (view: FutureView) => setActiveView(view);
  const handleJobAction = (_jobId: string, action: string) => {
    if (action.includes("decision")) setActiveView("decision");
    if (action.includes("evidence") || action.includes("review")) {
      routeToFutureSurface(action.includes("evidence") ? "review-evidence" : "claude-review");
    }
    if (action.includes("worktree") || action.includes("job")) {
      routeToFutureSurface("codex-queue");
    }
  };
  const appendCommandMessages = (messages: ConversationMessage[]) => {
    setLocalCommandMessages((current) => dedupeMessages([...current, ...messages]));
  };
  const appendDecisionMessages = (messages: ConversationMessage[]) => {
    setLocalDecisionMessages((current) => dedupeMessages([...current, ...messages]));
    setState((current) => ({
      ...current,
      decisionRoom: {
        ...current.decisionRoom,
        thread: dedupeMessages([...current.decisionRoom.thread, ...messages]),
      },
    }));
  };
  const handleCommandMessage = async (
    messageText: string,
    targetAgent: AgentType | "all"
  ) => {
    const now = new Date().toISOString();
    const pendingUserMessageId = `human-command-${Date.now()}`;
    const userMessage: ConversationMessage = {
      id: pendingUserMessageId,
      source: "human",
      content: messageText,
      timestamp: now,
      metadata: {
        targetAgent,
      },
    };

    appendCommandMessages([userMessage]);
    setActionStatus(`${agentLabel(targetAgent)} is reading Core truth before answering.`);
    setCommandPending(true);

    try {
      const messageForCore = commandAgentContext
        ? [
            `Agent Studio context: ${commandAgentContext.identity.displayName}`,
            `Agent id: ${commandAgentContext.identity.id}`,
            `Domain: ${commandAgentContext.identity.domain}`,
            `Role: ${commandAgentContext.capability.role}`,
            `Registration status: ${commandAgentContext.registrationStatus}`,
            `User command: ${messageText}`,
          ].join("\n")
        : messageText;
      const result = await askAuraConversation({
        messageText: messageForCore,
        targetAgent,
        sessionId: createCommandSessionId(targetAgent),
        recentMessages: localCommandMessages,
      });
      const respondedBy = result.channel
        ? fromAdminConsoleAgentTarget(result.channel.respondedBy)
        : targetAgent;
      const responseSource = respondedBy === "all" ? "aura" : respondedBy;
      if (result.persistedMessages.length > 0) {
        setLocalCommandMessages((current) =>
          dedupeMessages([
            ...current.filter((message) => message.id !== pendingUserMessageId),
            ...result.persistedMessages,
          ])
        );
      } else {
        appendCommandMessages([
          {
            id: `core-command-reply-${Date.now()}`,
            source: responseSource,
            agentName: agentLabel(respondedBy),
            content: result.replyText,
            timestamp: new Date().toISOString(),
            metadata: {
              coreEvent: "admin_console_aura_conversation",
              targetAgent,
              respondedBy,
              responderRuntime: result.channel?.responderRuntime,
              channelProxy: result.channel?.proxy,
            },
          },
        ]);
      }
      setActionStatus(`Core grounded response returned to ${agentLabel(targetAgent)}.`);
    } catch (error) {
      appendCommandMessages([
        {
          id: `core-command-error-${Date.now()}`,
          source: "risk-warning",
          content:
            error instanceof Error
              ? `Command could not reach Core conversation: ${error.message}.`
              : "Command could not reach Core conversation.",
          timestamp: new Date().toISOString(),
        },
      ]);
      setActionStatus("Command AURA could not reach Core conversation.");
    } finally {
      setCommandPending(false);
    }
  };
  const handleDecisionContribution = async (
    contribution: string,
    options?: {
      contributionKind?: "proposal" | "objection";
      authorityLevel?: "advisory" | "blocking_objection";
      blocksExecution?: boolean;
    }
  ) => {
    const text = contribution.trim();
    if (!text) return;
    let topicForRecord = state.decisionRoom.topics.find(
      (topic) => topic.id === state.decisionRoom.selectedTopicId
    );

    const pendingMessage: ConversationMessage = {
      id: `human-decision-${Date.now()}`,
      source: "human",
      content: text,
      timestamp: new Date().toISOString(),
    };
    appendDecisionMessages([pendingMessage]);
    setActionStatus("Recording Decision Room contribution through Core.");

    try {
      const liveResponse = await fetch("/operational-state.json", {
        cache: "no-store",
      });

      if (liveResponse.ok) {
        const liveState = await liveResponse.json();
        const adapted = adaptLiveOperationalState(liveState, operationalState);
        const liveTopic =
          adapted.decisionRoom.topics.find((topic) => topic.id === selectedTopicId) ??
          adapted.decisionRoom.topics[0];

        topicForRecord = liveTopic ?? topicForRecord;
        setState(mergeLocalMessages(adapted));
      }

      const recorded = await recordDecisionContribution({
        contribution: text,
        contributionKind: options?.contributionKind,
        authorityLevel: options?.authorityLevel,
        blocksExecution: options?.blocksExecution,
        topic: topicForRecord,
        evidenceReferences: currentDecisionEvidence,
      });
      appendDecisionMessages([
        {
          id: `core-decision-recorded-${recorded.id}`,
          source: "core",
          content: `Core recorded human contribution ${recorded.id}.`,
          timestamp: recorded.recordedAt,
          metadata: {
            coreEvent: "decision_room_contribution_recorded",
          },
        },
      ]);
      await refreshOperationalState();
      setActionStatus("Decision Room contribution persisted through Core.");
    } catch (error) {
      appendDecisionMessages([
        {
          id: `core-decision-error-${Date.now()}`,
          source: "risk-warning",
          content:
            error instanceof Error
              ? `Core could not record the contribution: ${error.message}.`
              : "Core could not record the contribution.",
          timestamp: new Date().toISOString(),
        },
      ]);
      setActionStatus("Decision Room contribution was not persisted.");
    }
  };
  const refreshAgentRegistry = async () => {
    const registry = await loadAdminAgentRegistry();
    setState((current) => ({
      ...current,
      agentRegistry: {
        ...current.agentRegistry,
        agents: registry.agents,
      },
    }));
    return registry;
  };
  const handleCreateAgent = async (request: AdminAgentCreateInput) => {
    setActionStatus("Creating governed draft agent through Core.");
    const agent = await createAdminAgent(request);
    await refreshAgentRegistry();
    setSelectedAgentProfileId(agent.identity.id);
    setActionStatus(`${agent.identity.displayName} was created through Core as a draft agent.`);
  };
  const handleRegisterAgent = async (agentId: string) => {
    setActionStatus("Registering agent through Core.");
    const agent = await registerAdminAgent(agentId);
    await refreshAgentRegistry();
    setSelectedAgentProfileId(agent.identity.id);
    setActionStatus(`${agent.identity.displayName} is now registered through Core.`);
  };
  const handleCreateTool = async (request: AdminToolCreateInput) => {
    setActionStatus("Creating tenant tool through Core.");
    const tool = await createAdminTool(request);
    await refreshTools();
    setSelectedToolId(tool.id);
    setActionStatus(`${tool.displayName} was created through Core.`);
  };
  const handleAssignToolAgent = async (request: {
    toolId: string;
    moduleIds: string[];
    agentId: string;
    auditorAgentId?: string;
    visibility?: "private" | "workspace" | "tenant";
  }) => {
    setActionStatus("Creating tool-agent daemon through Core.");
    const assignment = await createAdminToolAssignment(request);
    await refreshTools();
    setActionStatus(`Daemon ${assignment.daemon.id} was registered through Core.`);
  };
  const handleCreateToolLearningSession = async (
    request: AdminToolLearningSessionCreateInput
  ) => {
    setActionStatus("Creating tool learning session through Core.");
    const session = await createAdminToolLearningSession(request);
    await refreshTools();
    setActionStatus(`${session.title} is now captured as a governed learning session.`);
    return session;
  };
  const handleAppendToolLearningEvidence = async (
    request: AdminToolLearningEvidenceAppendInput
  ) => {
    setActionStatus("Persisting learning evidence through Core.");
    const session = await appendAdminToolLearningEvidence(request);
    await refreshTools();
    setActionStatus(`${session.title} evidence was persisted through Core.`);
    return session;
  };
  const handleUploadToolLearningArtifact = async (
    request: AdminToolLearningArtifactStoreInput
  ) => {
    setActionStatus("Uploading sanitized learning artifact through Core.");
    const session = await uploadAdminToolLearningArtifact(request);
    await refreshTools();
    setActionStatus(`${session.title} artifact was stored and attached through Core.`);
    return session;
  };
  const handlePromoteToolLearningMemory = async (
    request: AdminToolLearningMemoryPromotionInput
  ) => {
    setActionStatus("Updating memory promotion state through Core.");
    const session = await promoteAdminToolLearningMemory(request);
    await refreshTools();
    setActionStatus(`${session.title} memory promotion state is ${session.memoryWriteback.promotionStatus}.`);
    return session;
  };
  const handleCreateToolConsolidationRun = async (request: {
    assignmentId: string;
    agentId: string;
    toolId: string;
    scheduledFor?: string;
  }) => {
    setActionStatus("Scheduling cognitive consolidation through Core.");
    const run = await createAdminToolConsolidationRun(request);
    await refreshTools();
    setActionStatus(`Consolidation run ${run.id} is recorded through Core.`);
  };
  const handleAgentDecisionRoomInvite = async (
    agentId: string,
    agent: AgentProfile | null
  ) => {
    const selectedTopic = state.decisionRoom.topics.find(
      (topic) => topic.id === selectedTopicId
    );
    const topic = {
      id: selectedTopic?.id,
      title:
        selectedTopic?.title ??
        `${agent?.identity.displayName ?? agentId} participation decision`,
      status: selectedTopic?.status === "waiting-review" ? "waiting_review" : selectedTopic?.status,
      kind:
        agent?.identity.domain === "sap"
          ? "connector"
          : agent?.capability.role === "reviewer" || agent?.capability.role === "auditor"
          ? "review"
          : agent?.capability.role === "executor"
          ? "implementation"
          : "strategy",
      toolDomain:
        agent?.identity.domain === "climb-one"
          ? "climb_one"
        : agent?.identity.domain === "sap"
          ? "sap"
          : agent?.identity.domain === "aws" ||
            agent?.identity.domain === "browser" ||
            agent?.identity.domain === "desktop" ||
            agent?.identity.domain === "api"
          ? agent.identity.domain
          : "other",
      origin: "agent_studio",
      createdBy: "Founder Admin Console",
      participantIds: agent ? [agent.identity.id] : [agentId],
      tags: agent ? [agent.identity.domain, agent.capability.role] : [],
    };

    setActionStatus("Founder Agent is reasoning which agents belong in the Decision Room.");

    try {
      const candidateEvidence = await selectDecisionRoomAgentCandidates(topic);
      const candidateSummary = candidateEvidence.candidates
        .slice(0, 5)
        .map(
          (candidate) =>
            `${candidate.displayName} (${candidate.role}, ${candidate.domain}, ${candidate.registrationStatus}, score ${candidate.score}): ${candidate.reasons.join(" ")}`
        )
        .join("\n");
      const founderJudgment = await askAuraConversation({
        targetAgent: "founder-agent",
        sessionId: createCommandSessionId("founder-agent"),
        recentMessages: localCommandMessages,
        messageText: [
          "Reason cognitively about Decision Room participants.",
          "Do not answer from a template. Use the topic, agent authority, tool domain, registration status, and live operational context.",
          `Topic: ${topic.title}`,
          `Topic kind: ${topic.kind}`,
          `Tool domain: ${topic.toolDomain}`,
          `Requested agent: ${agent?.identity.displayName ?? agentId}`,
          "Eligible candidate evidence from Core:",
          candidateSummary || "No eligible candidates returned.",
          "Decide who should participate and explain why in natural operational language.",
        ].join("\n"),
      });
      const judgmentText = founderJudgment.replyText.trim();

      await handleDecisionContribution(
        judgmentText ||
          `${agent?.identity.displayName ?? agentId} was selected for this Decision Room from Core agent policy evidence.`,
        {
          contributionKind: "proposal",
          authorityLevel: "advisory",
        }
      );
      appendCommandMessages(founderJudgment.persistedMessages);
      setActionStatus("Founder Agent selected Decision Room participation from Core evidence.");
    } catch (error) {
      await handleDecisionContribution(
        `${agent?.identity.displayName ?? agentId} was proposed for Decision Room participation, but Founder reasoning could not complete: ${
          error instanceof Error ? error.message : "unknown error"
        }.`,
        {
          contributionKind: "proposal",
          authorityLevel: "advisory",
        }
      );
      setActionStatus("Agent invite recorded, but Founder participant reasoning failed.");
    }
  };
  const handleOperationalAction = async (action: string) => {
    const normalized = action.toLowerCase();
    try {
      if (normalized.includes("decision")) {
        setActiveView("decision");
        return;
      }
      if (normalized.includes("aura")) {
        setActiveView("command");
        return;
      }
      if (normalized.includes("evidence")) {
        routeToFutureSurface("review-evidence");
        return;
      }
      if (normalized.includes("claude") || normalized.includes("review")) {
        const label = await queueOperatorCommand("api_dry_run");
        routeToFutureSurface("claude-review");
        setActionStatus(label);
        await refreshAdminConsoleSnapshot();
        await refreshOperationalState();
        return;
      }
      if (normalized.includes("codex") || normalized.includes("job") || normalized.includes("worktree")) {
        const label = await queueOperatorCommand("browser_dry_run");
        routeToFutureSurface("codex-queue");
        setActionStatus(label);
        await refreshAdminConsoleSnapshot();
        await refreshOperationalState();
        return;
      }
      if (normalized.includes("hold")) {
        await handleDecisionContribution(
          "Hold production-impacting execution while local implementation, review, and evidence gathering continue.",
          {
            contributionKind: "objection",
            authorityLevel: "blocking_objection",
            blocksExecution: true,
          }
        );
        routeToFutureSurface("hold-deployment");
        return;
      }
      if (normalized.includes("approve")) {
        await handleDecisionContribution(
          "Founder proposes approval only for safe local execution. Production-impacting execution remains governed by Core approval gates.",
          {
            contributionKind: "proposal",
            authorityLevel: "advisory",
            blocksExecution: false,
          }
        );
        routeToFutureSurface("approve-execution");
        return;
      }
      if (normalized.includes("dispatch")) {
        const label = await dispatchNextOperatorCommand();
        setActionStatus(label);
        await refreshOperationalState();
        return;
      }
      if (normalized.includes("audit")) {
        routeToFutureSurface("audit-trail");
        return;
      }
      if (normalized.includes("memory") || normalized.includes("context")) {
        routeToFutureSurface("deep-context");
        return;
      }
      setActionStatus(`${action} is visible, but no Core route exists for it yet.`);
    } catch (error) {
      setActionStatus(
        error instanceof Error
          ? `${action} failed: ${error.message}`
          : `${action} failed.`
      );
    }
  };

  if (!liveStateReady) {
    return <CoreTruthLoadingSurface error={liveStateError} />;
  }

  return (
    <div className="flex h-[100dvh] w-screen flex-col overflow-hidden bg-background">
      {/* Top Operational Bar */}
      <TopOperationalBar
        topBar={state.topBar}
        userInitials={state.currentUser.initials}
      />

      {/* Main Layout */}
      <div className="flex min-h-0 flex-1 overflow-hidden">
        {/* Main Content Area */}
        <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
          {/* Contextual Action Bar */}
          <ContextualActionBar
            activeView={activeView}
            onViewChange={setActiveView}
          />

          {/* Screen Content */}
          {activeView === "console" && (
            <MainOperationalConsole
              brief={state.auraBrief}
              jobs={state.jobs}
              executionState={state.executionState}
              onReviewEvidence={() => routeToFutureSurface("review-evidence")}
              onApproveExecution={() => routeToFutureSurface("approve-execution")}
              onHoldDeployment={() => routeToFutureSurface("hold-deployment")}
              onOpenDecisionRoom={() => setActiveView("decision")}
              onCommandAura={() => setActiveView("command")}
              onOpenJob={() => routeToFutureSurface("codex-queue")}
              onOpenWorktree={() => routeToFutureSurface("codex-queue")}
              onRequestReview={() => routeToFutureSurface("claude-review")}
              onViewEvidence={() => routeToFutureSurface("review-evidence")}
              onViewAllJobs={() => routeToFutureSurface("codex-queue")}
              onJobAction={handleJobAction}
            />
          )}
          {activeView === "command" && (
            <CommandAura
              agents={state.agents}
              thread={localCommandMessages}
              context={state.commandThreads.context}
              suggestedCommands={state.commandThreads.suggestedCommands}
              selectedAgentId={selectedAgentId}
              agentContext={commandAgentContext}
              onSelectAgent={(agentId) => {
                setSelectedAgentId(agentId);
                setCommandAgentContext(null);
              }}
              onSendMessage={handleCommandMessage}
              onAttachEvidence={() => handleOperationalAction("Review Evidence")}
              onRequestClaudeReview={() => handleOperationalAction("Request Claude Review")}
              onRecordThroughCore={() =>
                handleDecisionContribution(
                  "Command AURA context should be recorded as Core evidence before it affects execution.",
                  { contributionKind: "proposal" }
                )
              }
              onHoldProduction={() => handleOperationalAction("Hold Deployment")}
              onAcceptRecommendation={() =>
                handleDecisionContribution(state.auraBrief.recommendation, {
                  contributionKind: "proposal",
                })
              }
              onSuggestedCommand={(command) =>
                handleCommandMessage(command.text, command.targetAgent)
              }
            />
          )}
          {activeView === "decision" && (
            <DecisionRoom
              topics={state.decisionRoom.topics}
              selectedTopicId={selectedTopicId}
              thread={state.decisionRoom.thread}
              intelligence={state.decisionRoom.intelligence}
              onSelectTopic={setSelectedTopicId}
              onCloseTopic={() => setSelectedTopicId(null)}
              onSendMessage={(message) =>
                handleDecisionContribution(message, { contributionKind: "proposal" })
              }
              onAttachEvidence={() => handleOperationalAction("Review Evidence")}
              onAskAura={() => setActiveView("command")}
              onRequestClaudeReview={() => handleOperationalAction("Request Claude Review")}
              onRecordThroughCore={() =>
                handleDecisionContribution(
                  "Human founder requests Core persistence of the current decision context.",
                  { contributionKind: "proposal" }
                )
              }
              onAcceptRecommendation={() =>
                handleDecisionContribution(state.auraBrief.recommendation, {
                  contributionKind: "proposal",
                })
              }
              onPauseDecision={() =>
                handleDecisionContribution("Pause this decision until evidence is refreshed.", {
                  contributionKind: "objection",
                  authorityLevel: "blocking_objection",
                  blocksExecution: true,
                })
              }
              onEscalateApproval={() =>
                handleDecisionContribution(
                  "Escalate this decision for founder or operations lead approval.",
                  { contributionKind: "proposal" }
                )
              }
              onSendToCodex={() => handleOperationalAction("Send Codex to Implement")}
            />
          )}
          {activeView === "agents" && (
            <AgentStudio
              agents={state.agentRegistry.agents}
              selectedAgentId={selectedAgentProfileId}
              filterStatus={agentFilterStatus}
              filterKind={agentFilterKind}
              onSelectAgent={setSelectedAgentProfileId}
              onFilterStatus={setAgentFilterStatus}
              onFilterKind={setAgentFilterKind}
              onCreateAgent={handleCreateAgent}
              onRegisterAgent={handleRegisterAgent}
              onOpenInCommandAura={(agentId) => {
                const agent = state.agentRegistry.agents.find(
                  (item) => item.identity.id === agentId
                );
                const target = resolveCommandTarget(agentId);
                setSelectedAgentId(target);
                setCommandAgentContext(agent ?? null);
                if (agent) {
                  appendCommandMessages([
                    {
                      id: `agent-context-${agent.identity.id}-${Date.now()}`,
                      source: "system",
                      content: `Agent Studio context opened: ${agent.identity.displayName}. Commands are routed through ${agentLabel(target)} until a dedicated runtime channel is available.`,
                      timestamp: new Date().toISOString(),
                      metadata: {
                        targetAgent: target,
                        coreEvent: "agent_studio_context_opened",
                      },
                    },
                  ]);
                }
                setActiveView("command");
              }}
              onInviteToDecisionRoom={(agentId) => {
                const agent = state.agentRegistry.agents.find(
                  (item) => item.identity.id === agentId
                );
                void handleAgentDecisionRoomInvite(agentId, agent ?? null);
                appendDecisionMessages([
                  {
                    id: `agent-invite-${Date.now()}`,
                    source: "core",
                    content: `${agent?.identity.displayName ?? agentId} was proposed for the active Decision Room context. Founder reasoning is selecting participants from Core policy evidence.`,
                    timestamp: new Date().toISOString(),
                    metadata: {
                      coreEvent: "agent_invited_to_decision_room",
                    },
                  },
                ]);
                setActiveView("decision");
              }}
            />
          )}
          {activeView === "tools" && (
            <ToolsStudio
              toolsState={toolsState}
              agents={state.agentRegistry.agents}
              selectedToolId={selectedToolId}
              loading={toolsLoading}
              onRefresh={() => {
                void refreshTools().catch(() => {
                  setActionStatus("Tools registry refresh failed.");
                });
              }}
              onSelectTool={setSelectedToolId}
              onCreateTool={handleCreateTool}
              onAssignAgent={handleAssignToolAgent}
              onCreateLearningSession={handleCreateToolLearningSession}
              onAppendLearningEvidence={handleAppendToolLearningEvidence}
              onUploadLearningArtifact={handleUploadToolLearningArtifact}
              onPromoteLearningMemory={handlePromoteToolLearningMemory}
              onQueueOperatorCommand={queueOperatorCommand}
              onCreateConsolidationRun={handleCreateToolConsolidationRun}
              onOpenCommandAura={(agentId) => {
                const target = agentId ? resolveCommandTarget(agentId) : "aura";
                setSelectedAgentId(target);
                setActiveView("command");
              }}
              onOpenDecisionRoom={() => setActiveView("decision")}
            />
          )}
          {futureSurface && (
            <FutureOperationalSurface
              surface={futureSurface}
              onBack={() => setActiveView("console")}
              onOpenDecisionRoom={() => setActiveView("decision")}
              onCommandAura={() => setActiveView("command")}
              onAction={handleOperationalAction}
            />
          )}
        </div>

        {/* Right Operational Truth Rail */}
        <OperationalTruthRail
          truthRail={state.truthRail}
          onToggleDeepContext={() => routeToFutureSurface("deep-context")}
        />
      </div>
      {actionStatus && (
        <div className="fixed bottom-20 left-1/2 z-50 w-[min(22rem,calc(100vw-2rem))] -translate-x-1/2 rounded-lg border border-accent/30 bg-card/95 px-4 py-2 text-xs text-foreground shadow-2xl backdrop-blur lg:bottom-4 lg:w-auto">
          {actionStatus}
        </div>
      )}
    </div>
  );
}

function CoreTruthLoadingSurface({ error }: { error: string | null }) {
  return (
    <div className="h-screen w-screen overflow-hidden bg-background text-foreground">
      <div className="flex h-10 items-center justify-between border-b border-border/30 px-6 text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
        <span>CLIMB ONE / AURA</span>
        <span>Loading Core truth</span>
      </div>
      <div className="flex h-[calc(100vh-2.5rem)] items-center justify-center">
        <section className="w-[520px] rounded-2xl border border-border/30 bg-card/45 p-8 shadow-2xl backdrop-blur">
          <div className="mb-6 h-16 w-16 rounded-full border border-accent/30 bg-accent/10 shadow-[0_0_60px_rgba(103,232,249,0.18)]" />
          <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
            AURA Admin Console
          </p>
          <h1 className="mt-3 text-3xl font-semibold tracking-tight">
            Reading live operational state
          </h1>
          <p className="mt-4 text-sm leading-6 text-muted-foreground">
            The console is waiting for CLIMB ONE Core before rendering operational metrics.
            No fallback readiness, blockers, approvals, or jobs are shown as truth.
          </p>
          {error && (
            <p className="mt-5 rounded-lg border border-amber-400/25 bg-amber-400/10 px-4 py-3 text-sm text-amber-100">
              {error}
            </p>
          )}
        </section>
      </div>
    </div>
  );
}

function createCoreUnavailableState(reason: string): typeof operationalState {
  return {
    ...operationalState,
    topBar: {
      ...operationalState.topBar,
      runtime: {
        ...operationalState.topBar.runtime,
        value: "Unavailable",
        status: "critical",
      },
      memory: {
        ...operationalState.topBar.memory,
        value: "Unknown",
        status: "warning",
      },
      provider: {
        ...operationalState.topBar.provider,
        value: "Unknown",
        status: "warning",
      },
      health: {
        ...operationalState.topBar.health,
        value: "Core unreachable",
        status: "critical",
      },
    },
    auraBrief: {
      ...operationalState.auraBrief,
      primaryAssessment:
        "CLIMB ONE Core is not reachable, so AURA is showing a degraded operating surface.",
      whatMattersNow: [
        {
          id: "wmn-core-unreachable",
          text: "Restore the Core/Admin Console API before treating operational state as live truth.",
          priority: "critical",
          icon: "alert",
          source: "runtime-warning",
          domain: "runtime",
        },
      ],
      belief:
        "The portal is available, but live operational truth cannot be verified until Core responds.",
      recommendation:
        "Keep the console open in degraded mode and restore the Admin Console API on port 4173.",
      executionReadiness: {
        score: 0,
        level: "unknown",
        blockers: [reason],
        safeActions: ["Inspect the console shell", "Restore Core API connectivity"],
        blockedActions: ["Treating visible operational state as verified live truth"],
      },
      evidenceConfidence: {
        level: "low",
        score: 0,
        reason,
        verifiedChecks: 0,
        totalChecks: 1,
      },
      requiredApprovalRole: null,
      requiredApprovalDescription: null,
    },
    truthRail: {
      ...operationalState.truthRail,
      blockers: {
        ...operationalState.truthRail.blockers,
        value: "1 runtime",
        status: "blocker",
      },
      approvals: {
        ...operationalState.truthRail.approvals,
        value: "Unknown",
        status: "warning",
      },
      activeJobs: {
        ...operationalState.truthRail.activeJobs,
        value: "Unknown",
        status: "warning",
      },
      memory: {
        ...operationalState.truthRail.memory,
        value: "Unknown",
        status: "warning",
      },
      readiness: {
        ...operationalState.truthRail.readiness,
        value: "Unverified",
        status: "critical",
      },
      codexQueue: {
        ...operationalState.truthRail.codexQueue,
        value: "Unknown",
        status: "warning",
      },
      claudeReview: {
        ...operationalState.truthRail.claudeReview,
        value: "Unknown",
        status: "warning",
      },
      runtime: {
        ...operationalState.truthRail.runtime,
        value: "Core unreachable",
        status: "critical",
      },
    },
    jobs: [],
    decisionRoom: {
      ...operationalState.decisionRoom,
      topics: [],
      selectedTopicId: null,
      thread: [],
      intelligence: null,
    },
    commandThreads: {
      ...operationalState.commandThreads,
      thread: [],
      context: {
        ...operationalState.commandThreads.context,
        currentState: "Core truth is unavailable.",
        currentBlocker: reason,
        recommendedAction: {
          text: "Restore Core API connectivity.",
          type: "hold",
        },
        memoryNotes: ["Degraded mode is not canonical operational truth."],
      },
    },
  };
}

function resolveCommandTarget(agentId: string): AgentType | "all" {
  if (agentId.includes("founder")) return "founder-agent";
  if (agentId.includes("codex")) return "codex";
  if (agentId.includes("claude")) return "claude";
  if (agentId.includes("core")) return "core";
  return "aura";
}

function dedupeMessages(messages: ConversationMessage[]): ConversationMessage[] {
  const seen = new Set<string>();
  return messages.filter((message) => {
    if (seen.has(message.id)) return false;
    seen.add(message.id);
    return true;
  });
}

function agentLabel(agent: AgentType | "all"): string {
  const labels: Record<AgentType | "all", string> = {
    aura: "AURA",
    "founder-agent": "Founder Agent",
    codex: "Codex",
    claude: "Claude",
    core: "Core",
    all: "All Agents",
  };
  return labels[agent];
}

function fromAdminConsoleAgentTarget(
  target: string
): AgentType | "all" {
  const agents: Record<string, AgentType | "all"> = {
    aura: "aura",
    founder_agent: "founder-agent",
    codex: "codex",
    claude: "claude",
    core: "core",
    all_agents: "all",
  };

  return agents[target] ?? "aura";
}
